import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { api } from "@/lib/api";
import { askFirst, confirmWith } from "@/lib/confirm";
import { useIsAdmin } from "@/lib/session";
import { LoadError } from "@/components/LoadError";
import { useToast } from "@/components/Toaster";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

export const TEST_RECIPIENTS_KEY = "email.test_recipients";

/** One address, the way the server checks it (internal/system/settings_rules.go emailList), plus a dot in the domain. */
export function validTestAddress(a: string): boolean {
  return /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/.test(a);
}

/**
 * Settings, Email: the addresses test sends may go to (setting email.test_recipients). Broadcast and
 * automation tests can only reach these (and Amazon's test mailbox), so a test never reaches a customer.
 */
export function TestRecipientsCard() {
  const isAdmin = useIsAdmin();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ["settings"], queryFn: () => api<{ settings: { key: string; value: unknown; description?: string | null }[] }>("/api/v1/settings") });
  const stored = q.data?.settings.find((s) => s.key === TEST_RECIPIENTS_KEY);
  const saved = Array.isArray(stored?.value) ? (stored!.value as unknown[]).filter((x): x is string => typeof x === "string") : [];
  const [list, setList] = useState<string[] | null>(null);
  const [typed, setTyped] = useState("");
  const [problem, setProblem] = useState("");
  const [ask, setAsk] = useState<{ message: string; token?: string } | null>(null);
  const shown = list ?? saved;
  const dirty = list !== null && JSON.stringify(list) !== JSON.stringify(saved);
  useEffect(() => setAsk(null), [list]);

  const add = () => {
    const a = typed.trim();
    if (!a) return;
    if (!validTestAddress(a)) return setProblem(`"${a}" isn't an email address.`);
    if (shown.some((x) => x.toLowerCase() === a.toLowerCase())) return setProblem(`${a} is already on the list.`);
    setList([...shown, a]);
    setTyped("");
    setProblem("");
  };
  const remove = (a: string) => setList(shown.filter((x) => x !== a));

  const path = `/api/v1/settings/${encodeURIComponent(TEST_RECIPIENTS_KEY)}`;
  const body = () => ({ value: shown, description: stored?.description || "Brandon's own addresses for test sends (broadcasts and automations)." });
  const save = useMutation({
    // Email settings ask first (audit H-2): show the server's sentence, then save with its confirm token.
    mutationFn: () => (ask ? confirmWith(path, body(), ask.token, "PUT") : askFirst(path, body(), "PUT")),
    onSuccess: (r) => {
      if (!r.done) return setAsk({ message: r.message, token: r.token });
      void qc.invalidateQueries({ queryKey: ["settings"] });
      void qc.invalidateQueries({ queryKey: ["email-test-recipients"] });
      setList(null);
      setAsk(null);
      toast("Saved the test email addresses");
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Email: test email addresses</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-[var(--color-muted-foreground)]">
          Test sends of broadcasts and automation emails can only go to these addresses (and Amazon's test mailbox), so a test never reaches a customer. Use Brandon's own
          addresses only.
        </p>
        {q.isLoading && <Skeleton className="h-16" />}
        {q.error && <LoadError className="px-0" what="settings" error={q.error} onRetry={() => q.refetch()} />}
        {q.data && (
          <>
            {shown.length === 0 ? (
              <p className="text-[var(--color-warning-fg)]">The list is empty, so test sends can't go anywhere yet.</p>
            ) : (
              <ul className="space-y-1" aria-label="Test email addresses">
                {shown.map((a) => (
                  <li key={a} className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate">{a}</span>
                    {isAdmin && (
                      <Button size="sm" variant="ghost" aria-label={`Remove ${a}`} onClick={() => remove(a)}>
                        <X /> Remove
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {isAdmin ? (
              <form
                className="flex flex-wrap items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  add();
                }}
              >
                <Input
                  className="h-8 w-72 max-w-full"
                  inputMode="email"
                  autoComplete="off"
                  value={typed}
                  onChange={(e) => (setTyped(e.target.value), setProblem(""))}
                  placeholder="name@creuniversity.com"
                  aria-label="Add a test email address"
                />
                <Button type="submit" size="sm" variant="outline" disabled={!typed.trim()}>
                  <Plus /> Add
                </Button>
              </form>
            ) : (
              <p className="text-xs text-[var(--color-warning-fg)]">Only an admin can change this list.</p>
            )}
            {problem && <p className="text-xs text-[var(--color-danger-fg)]">{problem}</p>}
            {ask && (
              <div role="alert" className="rounded-md bg-[var(--color-warning-bg)] px-3 py-2 text-xs text-[var(--color-warning-fg)]">
                {ask.message}
              </div>
            )}
            {save.error && <p className="text-xs text-[var(--color-danger-fg)]">{save.error.message}</p>}
            {isAdmin && dirty && (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
                  {ask ? "Yes, save the list" : "Save the list"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => (setList(null), setProblem(""))}>
                  Undo changes
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
